import React, { useId, useMemo, useState } from "react";
import { SectionHeader } from "../components/section-header.tsx";
import "../../assets/fa-icons/css/fontawesome.css";
import "../../assets/fa-icons/css/sharp-solid.css";
import { FontStyles } from "../utils/font-styles.ts";
import { cn } from "../utils/cn.ts";
import { MapProxy } from "../components/map/map-proxy.tsx";
import type { MapMarker } from "../components/map/map-marker.ts";
import { TextLink } from "../components/text-link.tsx";
import { focusStylesOutline } from "../utils/focus-styles.ts";
import { usePropertyDetailsContext } from "./contexts/property-details-context.tsx";

type SchoolShape = number[][][];
type SchoolShapeCache = Record<number, SchoolShape | null>;

interface SchoolApiResponse {
  hideShape: boolean;
  shapes: SchoolShape;
}

const formatValue = (value: number) => {
  return value % 1 === 0 ? value.toString() : value.toFixed(1);
};

const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

  const lat1Rad = toRadians(lat1);
  const lat2Rad = toRadians(lat2);

  const deltaLat = toRadians(lat2 - lat1);
  const deltaLng = toRadians(lng2 - lng1);

  const a = Math.sin(deltaLat / 2) ** 2 + Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(deltaLng / 2) ** 2;

  const b = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const earthRadius = 6371e3; // meters
  return earthRadius * b;
};

async function fetchSchoolShape(id: number): Promise<SchoolShape | null> {
  const response = await fetch(`/api/schools/${String(id)}`);
  // 404 is the endpoint's "no catchment record for this school" — a stable answer, worth caching.
  if (response.status === 404) return null;
  // Anything else is likely transient, so throw: the caller caches nothing and the next click retries.
  if (!response.ok) throw new Error(`School catchment request failed with status ${String(response.status)}`);
  const data = (await response.json()) as SchoolApiResponse;
  if (data.hideShape) return null;
  return data.shapes;
}

export const SchoolCatchments: React.FC = () => {
  const { schools = [], lat = 0, lon = 0, displayAddress = "" } = usePropertyDetailsContext();
  const [activeIndex, setActiveIndex] = useState(-1);
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const [shapeCache, setShapeCache] = useState<SchoolShapeCache>({});
  const id = useId();

  const sortedSchools = useMemo(() => {
    return [...schools].sort(
      (a, b) => calculateDistance(a.lat, a.lng, lat, lon) - calculateDistance(b.lat, b.lng, lat, lon),
    );
  }, [schools, lat, lon]);

  if (!schools.length) return null;

  // The map's marker list has the property marker appended after the schools, so an active index
  // of -1 (nothing selected) or sortedSchools.length (the property marker) has no school behind it.
  const schoolAt = (index: number) => (index >= 0 && index < sortedSchools.length ? sortedSchools[index] : undefined);

  const fetchAndCacheShape = (index: number) => {
    const school = schoolAt(index);
    if (!school) return;
    if (school.id in shapeCache) return;
    fetchSchoolShape(school.id)
      .then((shape) => {
        setShapeCache((prev) => ({ ...prev, [school.id]: shape }));
      })
      .catch(console.error);
  };

  const handleSchoolClick = (index: number) => {
    const nextIndex = activeIndex === index ? -1 : index;
    setActiveIndex(nextIndex);
    fetchAndCacheShape(nextIndex);
  };

  const handleMapActiveChange = (index: number) => {
    setActiveIndex(index);
    fetchAndCacheShape(index);
  };

  const activeSchool = schoolAt(activeIndex);
  const activeOutline = activeSchool ? (shapeCache[activeSchool.id] ?? undefined) : undefined;

  const schoolMarkers: MapMarker[] = sortedSchools.map((school) => ({
    location: { lat: school.lat, lon: school.lng },
    markerType: "school" as const,
    popup: {
      title: school.name,
      infoItems: [{ icon: "fa-sharp fa-solid fa-building", text: school.address }],
    },
  }));

  const propertyMarker: MapMarker = {
    location: { lat, lon },
    markerType: "home",
    popup: {
      infoItems: [{ icon: "fa-sharp fa-solid fa-house-chimney", text: displayAddress }],
    },
  };

  const markers = [...schoolMarkers, propertyMarker];
  return (
    <section className={"flex flex-col gap-3 md:gap-3.5"} aria-labelledby={id}>
      <SectionHeader id={id} text={"School catchments"} as={"h2"} className={FontStyles.h5} />
      <MapProxy
        markers={markers}
        activeIndex={activeIndex}
        hoverIndex={hoveredIndex}
        containerClass={"h-[300px] md:h-[440px] shadow-espc"}
        onActiveChange={handleMapActiveChange}
        onHoverChange={setHoveredIndex}
        activeOutline={activeOutline}
      />
      <div className={"flex flex-col gap-3"}>
        {sortedSchools.map((school, index) => {
          const distanceKilometers = calculateDistance(school.lat, school.lng, lat, lon) / 1000;
          const formattedDistanceKilometers = formatValue(distanceKilometers);
          const kmToMilesRatio = 0.6214;
          const formattedDistanceMiles = formatValue(distanceKilometers * kmToMilesRatio);
          return (
            <button
              key={school.name}
              onMouseOver={() => setHoveredIndex(index)}
              onMouseOut={() => setHoveredIndex(-1)}
              onClick={() => handleSchoolClick(index)}
              className={cn(
                `flex justify-between border rounded-md p-3 md:py-3.5 md:px-5`,
                `cursor-pointer transition-colors duration-200 bg-cloud border-neutral-200`,
                index === activeIndex && "bg-loch-50",
                focusStylesOutline,
                (index === activeIndex || index === hoveredIndex) && "border-loch outline outline-1 outline-loch",
              )}
            >
              <div className={"flex gap-3.5 w-full"}>
                <div className={"bg-loch-500 rounded-full hidden md:flex"}>
                  <i className={"fa-solid fa-sharp fa-graduation-cap text-neutral-white p-2.5"} aria-hidden="true" />
                </div>
                <div className={"flex flex-col gap-2.5 w-full"}>
                  <div className={"flex justify-between items-start gap-2.5"}>
                    <span
                      className={`text-slate font-semibold leading-none md:leading-none text-left ${FontStyles["body-md"]}`}
                    >
                      {school.name}
                    </span>
                    <div
                      className={`flex items-center text-light-slate leading-none md:leading-none whitespace-nowrap ${FontStyles["body-s"]}`}
                    >
                      {formattedDistanceKilometers} km / {formattedDistanceMiles} mi
                    </div>
                  </div>
                  <a
                    className={`flex w-fit text-loch gap-1.5 font-medium leading-none md:leading-none ${FontStyles["body-s"]} ${focusStylesOutline}`}
                    href={school.catchmentSearchLink}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className={"flex md:hidden"}>View properties in this catchment</span>
                    <span className={"hidden md:flex"}>View other properties in this school's catchment</span>
                    <i className={"fa-regular fa-sharp fa-arrow-right text-body-xs"} aria-hidden="true" />
                  </a>
                </div>
              </div>
            </button>
          );
        })}
        <div className={`pt-1.5 md:pt-3 ${FontStyles["body-s"]}`}>
          * School catchment data is provided for information purposes only and ESPC UK Ltd does not accept any
          responsibility for the accuracy or completeness of the data. You should always contact the local authority
          responsible for the provision of education in a property's location, and more information on particular school
          catchment areas can be found via the links <TextLink href="/advice/tools/schools">here</TextLink>.
        </div>
      </div>
    </section>
  );
};
